import { LEVELS_META, DIFFICULTY_LIST, TIER_ORDER, FEATURED_CITIES, FEATURED_LEVELS } from "@/game/config";
import { useEffect, useState } from "react";
import { MenuBackdrop } from "@/components/MenuBackdrop";
import { FitScreen } from "@/components/FitScreen";
import { Lock, Lightbulb, Star, ArrowLeft, Swords, Check, Sparkles, MapPin, Share2, Gift } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { shareNicaBros, featuredShareUrl } from "@/lib/share";
import { toast } from "sonner";
import { api } from "@/lib/api";
import GiftCardBoard from "@/components/GiftCardBoard";

export default function LevelSelect({ character, unlocked, bonusUnlocked, oceanUnlocked, diffKey, onDiff, onSelect, onBonus, onOceanBonus, onRodeoBonus, onRodeoLevel, onFeatured, onBack, onSuggest, onAuth, onChallenge }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  useEffect(() => { api.giftcardChallenges().then((r) => setChallenges(r.data.challenges || [])).catch(() => {}); }, []);
  const levelFor = (ch) => FEATURED_LEVELS.find((l) => l.index === ch.level_index) || LEVELS_META.find((l) => l.index === ch.level_index);
  const base = `${process.env.PUBLIC_URL || ""}/game-assets`;
  const diffColor = { facil: "#4CAF50", normal: "#0067C6", extremo: "#E91E63" };
  const completedTiers = user?.completed_tiers || {};
  let rodeoBest = 0;
  try { rodeoBest = parseInt(localStorage.getItem("nicabros_rodeo_best_coins") || "0", 10) || 0; } catch (e) {}
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center scanlines">
      <MenuBackdrop themeIndex={2} />
      <div className="absolute inset-0 bg-black/70" />

      <button
        data-testid="level-back-top-btn"
        onClick={onBack}
        className="fixed top-3 right-3 z-30 pixel-btn bg-[#222] text-white px-3 py-2 text-[11px] sm:text-sm flex items-center gap-1.5"
      >
        <ArrowLeft size={15} strokeWidth={2.5} /> {t("changeChar")}
      </button>

      <div className="relative z-10 w-full h-full overflow-y-auto" data-testid="level-scroll">
      <div className="w-full max-w-6xl mx-auto px-4 py-4 flex flex-col items-center min-h-full">
        <p className="font-vt text-xl text-white mb-1">
          {t("playingWith")} <span style={{ color: character.colors.accent }}>{character.name}</span>
        </p>
        <h2 className="font-pixel text-lg md:text-2xl text-[#FFEB3B] pixel-stroke mb-3 text-center">{t("chooseLevel")}</h2>

        {/* Difficulty selector */}
        <div className="mb-4 flex flex-col items-center" data-testid="difficulty-selector">
          <p className="font-pixel text-[10px] text-white/80 mb-2">{t("difficulty")}</p>
          <div className="flex gap-2 md:gap-3">
            {DIFFICULTY_LIST.map((d) => {
              const active = diffKey === d.id;
              return (
                <button
                  key={d.id}
                  data-testid={`difficulty-${d.id}`}
                  onClick={() => onDiff(d.id)}
                  className="pixel-btn px-5 py-2.5 text-xs md:text-sm transition-transform"
                  style={{
                    backgroundColor: active ? diffColor[d.id] : "#1b1b1b",
                    color: "#fff",
                    transform: active ? "translateY(-2px) scale(1.06)" : "none",
                    opacity: active ? 1 : 0.7,
                    borderColor: diffColor[d.id],
                  }}
                  title={t(`diff${d.id.charAt(0).toUpperCase() + d.id.slice(1)}Desc`)}
                >
                  {t(`diff${d.id.charAt(0).toUpperCase() + d.id.slice(1)}`)}
                </button>
              );
            })}
          </div>
          <p className="font-vt text-sm text-white/70 mt-2 h-5" data-testid="difficulty-desc">
            {t(`diff${diffKey.charAt(0).toUpperCase() + diffKey.slice(1)}Desc`)}
          </p>
        </div>

        <div className="w-full flex items-center gap-3 mb-3" data-testid="levels-section-header">
          <span className="h-px flex-1 bg-white/20" />
          <span className="font-pixel text-[11px] sm:text-sm text-[#FFEB3B] pixel-stroke tracking-wide">{t("levelsSection")}</span>
          <span className="h-px flex-1 bg-white/20" />
        </div>

        <div className="grid grid-cols-3 gap-3 w-full">
          {LEVELS_META.map((lv) => {
            const isLocked = lv.index > unlocked && !lv.alwaysUnlocked;
            return (
              <div key={lv.index} className="relative">
                {!isLocked && (
                  <div className="level-glow absolute -inset-1.5 rounded-2xl blur-lg pointer-events-none z-0"
                    style={{ background: diffColor[diffKey] || "#4CAF50", animationDelay: `${lv.index * 0.25}s` }} aria-hidden />
                )}
              <button
                data-testid={`level-card-${lv.index + 1}`}
                disabled={isLocked}
                onClick={() => !isLocked && onSelect(lv)}
                className="relative z-10 w-full pixel-card p-0 overflow-hidden text-left flex flex-col disabled:opacity-60 hover:-translate-y-1 transition-transform disabled:hover:translate-y-0"
                style={{ backgroundColor: "#fff" }}
              >
                <div className="relative h-20 sm:h-28 lg:h-36 w-full shrink-0 overflow-hidden">
                  <img src={`${base}/${lv.cardBg || lv.bg}.webp`} alt={lv.name} draggable={false}
                    style={lv.cardPos ? { objectPosition: lv.cardPos } : undefined}
                    className={`absolute inset-0 w-full h-full object-cover ${isLocked ? "grayscale" : ""}`} />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.05))" }} />
                  {lv.bossLevel && (
                    <span data-testid={`level-boss-badge-${lv.index + 1}`} className="absolute top-1.5 left-1.5 z-10 font-pixel text-[7px] sm:text-[8px] bg-[#c62828] text-white px-1.5 py-1 rounded-full flex items-center gap-1 pixel-stroke">
                      <Swords size={10} strokeWidth={2.5} /> {t("bossLevelBadge", "SPECIAL BOSS")}
                    </span>
                  )}
                  {lv.index === 2 && !(user?.unlocked_chars || []).includes("gigantona") && (
                    <span data-testid="level-freechar-ribbon-3" className="absolute top-1.5 right-1.5 z-10 font-pixel text-[7px] sm:text-[8px] bg-[#E91E63] text-white px-1.5 py-1 rounded-full pixel-stroke animate-pulse">
                      {t("freeCharBadge")}
                    </span>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center font-pixel text-white text-2xl sm:text-3xl pixel-stroke">
                    {isLocked ? <Lock size={36} strokeWidth={3} /> : lv.short}
                  </div>
                </div>
                <div className="p-2.5 sm:p-3 bg-white flex-1">
                  <p className="font-pixel text-[9px] sm:text-[10px] text-black">{t("level")} {lv.index + 1}</p>
                  <p className="font-vt text-sm sm:text-base text-gray-900 leading-tight mt-0.5">{lv.name}</p>
                  {lv.index === 2 && !(user?.unlocked_chars || []).includes("gigantona") && (
                    <p data-testid="level3-reward-teaser" className="font-vt text-sm text-[#E91E63] font-bold leading-tight mt-1">
                      {t("level3Reward")}
                    </p>
                  )}
                  {lv.influencedBy && (
                    <p className="font-pixel text-sm text-[#0a47d6] mt-2 flex items-center flex-wrap gap-1.5 leading-relaxed" data-testid={`level-influenced-${lv.index + 1}`}>
                      <Star size={16} strokeWidth={3} className="text-[#FFC107] fill-[#FFC107]" /> {t("influencedBy", "Influenced by")}:{" "}
                      {lv.influencedByUrl ? (
                        <span
                          role="link"
                          tabIndex={0}
                          data-testid={`level-influenced-link-${lv.index + 1}`}
                          onClick={(e) => { e.stopPropagation(); window.open(lv.influencedByUrl, "_blank", "noopener"); }}
                          className="text-[#1DB954] underline underline-offset-2 hover:text-[#14833b] cursor-pointer"
                        >
                          {lv.influencedBy}
                        </span>
                      ) : lv.influencedBy}
                    </p>
                  )}
                  <div className="flex items-end justify-between mt-2 gap-2">
                    {isLocked ? (
                      <p className="font-vt text-lg" style={{ color: "#999" }}>🔒 {t("locked")}</p>
                    ) : (
                      <span data-testid={`level-play-pill-${lv.index + 1}`}
                        className="play-pill inline-block font-pixel text-[9px] sm:text-[10px] text-white bg-[#4CAF50] rounded-full px-3 py-1.5 pixel-stroke">
                        ▶ {t("play")}
                      </span>
                    )}
                    {user && !isLocked && (
                      <div className="flex items-end gap-2 shrink-0" data-testid={`level-tiers-${lv.index + 1}`}>
                        {TIER_ORDER.map((tier) => {
                          const done = (completedTiers[String(lv.index)] || []).includes(tier);
                          const label = t(`diff${tier.charAt(0).toUpperCase() + tier.slice(1)}`);
                          return (
                            <div key={tier} className="flex flex-col items-center gap-0.5" title={label}
                              data-testid={`level-${lv.index + 1}-tier-${tier}${done ? "-done" : ""}`}>
                              <span className="w-5 h-5 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: done ? diffColor[tier] : "#d5d5d5", boxShadow: done ? `0 0 0 1.5px ${diffColor[tier]}` : "none" }}>
                                {done && <Check size={12} strokeWidth={3.5} className="text-white" />}
                              </span>
                              <span className="font-vt text-[10px] leading-none uppercase tracking-tight" style={{ color: done ? diffColor[tier] : "#9aa0a6" }}>{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
              </div>
              </button>
              </div>
            );
          })}
        </div>

        {/* Rodeo LEVEL (Level 7) — styled to match the numbered level cards */}
        <button
          data-testid="rodeo-level-card"
          onClick={() => onRodeoLevel && onRodeoLevel()}
          className="mt-6 w-full max-w-sm mx-auto pixel-card p-0 overflow-hidden text-left flex flex-col hover:-translate-y-1 transition-transform"
          style={{ backgroundColor: "#fff" }}
        >
          <div className="relative h-28 sm:h-36 w-full shrink-0 overflow-hidden">
            <img src={`${base}/rodeo_level_card.webp`} alt="Corrida de Toros" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.05))" }} />
            <div className="absolute inset-0 flex items-center justify-center font-pixel text-white text-2xl sm:text-3xl pixel-stroke">1-7</div>
            <span data-testid="rodeo-boss-badge" className="absolute top-2 left-3 z-10 font-pixel text-[8px] sm:text-[9px] bg-[#c62828] text-white px-2 py-1 rounded-full flex items-center gap-1 pixel-stroke">
              <Swords size={11} strokeWidth={2.5} /> {t("bossLevelBadge", "SPECIAL BOSS")}
            </span>
            {rodeoBest > 0 && (
              <span data-testid="rodeo-best-badge" className="absolute top-2 right-3 font-pixel text-[9px] sm:text-[10px] bg-[#FFD54F] text-black px-2 py-1 rounded-full">
                🏆 {t("rodeoBest", "Mejor")}: C$ {rodeoBest}
              </span>
            )}
            {!(user?.unlocked_chars || []).includes("enanocabezon") && (
              <span data-testid="level-freechar-ribbon-7" className="absolute bottom-2 left-3 z-10 font-pixel text-[8px] sm:text-[9px] bg-[#E91E63] text-white px-2 py-1 rounded-full pixel-stroke animate-pulse">
                {t("freeCharBadge")}
              </span>
            )}
          </div>
          <div className="p-2.5 sm:p-3 bg-white flex-1">
            <p className="font-pixel text-[9px] sm:text-[10px] text-black">{t("level")} 7</p>
            <p className="font-vt text-sm sm:text-base text-gray-900 leading-tight mt-0.5">{t("rodeoLevelCardName", "Corrida de Toros")}</p>
            {!(user?.unlocked_chars || []).includes("enanocabezon") && (
              <p data-testid="level7-reward-teaser" className="font-vt text-sm text-[#E91E63] font-bold leading-tight mt-1">{t("level7Reward")}</p>
            )}
            <div className="mt-2">
              <span data-testid="level-play-pill-7" className="play-pill inline-block font-pixel text-[9px] sm:text-[10px] text-white bg-[#4CAF50] rounded-full px-3 py-1.5 pixel-stroke">▶ {t("play")}</span>
            </div>
          </div>
        </button>

        {/* Featured Special Levels — promo levels grouped by city (before Bonus) */}
        <div className="w-full flex items-center gap-3 mt-10 mb-3" data-testid="featured-levels-section-header">
          <span className="h-px flex-1 bg-[#00d8ff]/40" />
          <span className="font-pixel text-[11px] sm:text-sm text-[#00d8ff] pixel-stroke tracking-wide flex items-center gap-1.5">
            <Sparkles size={14} strokeWidth={2.5} /> {t("featuredLevelsSection", "FEATURED SPECIAL LEVELS")}
          </span>
          <span className="h-px flex-1 bg-[#00d8ff]/40" />
        </div>
        {FEATURED_CITIES.map((city) => (
          <div key={city.id} className="w-full max-w-4xl" data-testid={`featured-city-${city.id}`}>
            <p className="font-vt text-lg text-white/90 flex items-center justify-center gap-1.5 mb-2">
              <MapPin size={16} strokeWidth={2.5} className="text-[#00d8ff]" /> {city.name}
            </p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-2">
              {city.levels.map((lv) => (
                <div key={lv.index} className="relative w-[calc(50%-0.5rem)] md:w-[calc(33.333%-0.75rem)]">
                  <div className="level-glow absolute -inset-1.5 rounded-2xl blur-lg pointer-events-none z-0" style={{ background: "#00d8ff" }} aria-hidden />
                  <button
                    data-testid={`featured-level-card-${lv.index}`}
                    onClick={() => onFeatured && onFeatured(lv)}
                    className="relative z-10 w-full pixel-card p-0 overflow-hidden text-left flex flex-col hover:-translate-y-1 transition-transform"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <div className="relative h-28 sm:h-36 w-full shrink-0 overflow-hidden">
                      <img src={`${base}/${lv.cardBg || lv.bg}.webp`} alt={lv.name} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.05))" }} />
                      <span className="absolute top-2 left-3 z-10 font-pixel text-[8px] sm:text-[9px] bg-[#00b8d4] text-white px-2 py-1 rounded-full flex items-center gap-1 pixel-stroke">
                        <Sparkles size={11} strokeWidth={2.5} /> {t("featuredBadge", "FEATURED")}
                      </span>
                      {lv.coupon && (
                        <span data-testid={`featured-coupon-ribbon-${lv.index}`} className="absolute bottom-2 right-3 z-10 font-pixel text-[8px] sm:text-[9px] bg-[#FFD54F] text-black px-2 py-1 rounded-full pixel-stroke animate-pulse">
                          🎟️ {lv.coupon.percent}% {t("couponOff", "OFF")}
                        </span>
                      )}
                    </div>
                    <div className="p-2.5 sm:p-3 bg-white flex-1">
                      <p className="font-pixel text-[9px] sm:text-[10px] text-[#00838f]">{t("featuredLabel", "ESPECIAL")}</p>
                      <p className="font-vt text-sm sm:text-base text-gray-900 leading-tight mt-0.5">{lv.name}</p>
                      {lv.coupon && (
                        <p data-testid={`featured-coupon-teaser-${lv.index}`} className="font-vt text-sm text-[#c28800] font-bold leading-tight mt-1">
                          {t("couponTeaser")(lv.coupon.percent)}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <span data-testid={`featured-play-pill-${lv.index}`} className="play-pill inline-block font-pixel text-[9px] sm:text-[10px] text-white bg-[#4CAF50] rounded-full px-3 py-1.5 pixel-stroke">▶ {t("play")}</span>
                        <button
                          type="button"
                          data-testid={`featured-share-btn-${lv.index}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = featuredShareUrl(lv);
                            const text = (lang === "es")
                              ? `¡Jugá el nivel de ${lv.sponsorName || lv.name} en Nica Bros y ganá ${lv.coupon ? lv.coupon.percent + "% de descuento" : "un premio"} en tu próxima cuenta! 🇳🇮🎮`
                              : `Play the ${lv.sponsorName || lv.name} level in Nica Bros and get ${lv.coupon ? lv.coupon.percent + "% off" : "a prize"} your next bill! 🇳🇮🎮`;
                            shareNicaBros({ text, url }).then((r) => {
                              if (r === "copied") toast.success(lang === "es" ? "¡Link copiado, compartilo!" : "Link copied — share it!");
                            });
                          }}
                          className="inline-flex items-center gap-1 font-pixel text-[8px] sm:text-[9px] text-white bg-[#0067C6] rounded-full px-2.5 py-1.5 pixel-stroke hover:-translate-y-0.5 transition-transform"
                        >
                          <Share2 size={11} strokeWidth={2.5} /> {t("share", "Share")}
                        </button>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Sponsored Gift Card Challenges — one board per active challenge (Extreme mode) */}
        {challenges.length > 0 && (
          <div className="w-full max-w-4xl" data-testid="giftcard-section">
            <div className="w-full flex items-center gap-3 mt-6 mb-3" data-testid="giftcard-section-header">
              <span className="h-px flex-1 bg-[#FFD54F]/40" />
              <span className="font-pixel text-[11px] sm:text-sm text-[#FFD54F] pixel-stroke tracking-wide flex items-center gap-1.5">
                <Gift size={14} strokeWidth={2.5} /> {lang === "es" ? "RETOS GIFT CARD" : "GIFT CARD CHALLENGES"}
              </span>
              <span className="h-px flex-1 bg-[#FFD54F]/40" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {challenges.map((ch) => (
                <div key={ch.id} className="relative">
                  <div className="level-glow absolute -inset-1.5 rounded-2xl blur-lg pointer-events-none z-0" style={{ background: "#0067C6" }} aria-hidden />
                  <GiftCardBoard challenge={ch} onAuth={onAuth} onPlay={levelFor(ch) && onChallenge ? (c) => onChallenge(levelFor(c), c) : undefined} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bonus rounds — Volcano + Ocean Dive + Rodeo */}
        <div className="w-full flex items-center gap-3 mt-10 mb-3" data-testid="bonus-levels-section-header">
          <span className="h-px flex-1 bg-[#FFB300]/40" />
          <span className="font-pixel text-[11px] sm:text-sm text-[#FFB300] pixel-stroke tracking-wide flex items-center gap-1.5">
            <Star size={14} strokeWidth={2.5} className="fill-[#FFB300]" /> {t("bonusLevelsSection")}
          </span>
          <span className="h-px flex-1 bg-[#FFB300]/40" />
        </div>
        <div className="w-full max-w-4xl grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {/* Volcano BONUS level — unlocked after finishing all 3 levels */}
        <div className="relative">
          {bonusUnlocked && <div className="level-glow absolute -inset-1.5 rounded-2xl blur-lg pointer-events-none z-0" style={{ background: "#FF5722" }} aria-hidden />}
        <button
          data-testid="bonus-level-card"
          disabled={!bonusUnlocked}
          onClick={() => bonusUnlocked && onBonus && onBonus()}
          className="relative z-10 w-full pixel-card p-0 overflow-hidden text-left flex flex-col disabled:opacity-60 hover:-translate-y-1 transition-transform disabled:hover:translate-y-0"
          style={{ backgroundColor: "#fff" }}
        >
          <div className="relative h-28 sm:h-36 w-full shrink-0 overflow-hidden">
            <img src={`${base}/bonus_volcano_card.webp`} alt="Volcano" draggable={false}
              className={`absolute inset-0 w-full h-full object-cover ${bonusUnlocked ? "" : "grayscale"}`} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.05))" }} />
            <span className="absolute top-2 left-3 font-pixel text-[10px] text-[#FFEB3B] pixel-stroke">★ BONUS ★</span>
            {!bonusUnlocked && <div className="absolute inset-0 flex items-center justify-center"><Lock size={44} strokeWidth={3} className="text-white" /></div>}
          </div>
          <div className="p-4 bg-white flex-1">
            <p className="font-pixel text-[10px] text-[#FF5722]">{t("volcanoBonus")}</p>
            <p className="font-vt text-xl text-gray-900 leading-tight mt-1">{t("volcanoBonusName")}</p>
            {bonusUnlocked ? (
              <span className="play-pill inline-block font-pixel text-[9px] sm:text-[10px] text-white bg-[#4CAF50] rounded-full px-3 py-1.5 pixel-stroke mt-2">▶ {t("playBonus")}</span>
            ) : (
              <p className="font-vt text-base mt-2 text-[#999]">🔒 {t("bonusLockedHint")}</p>
            )}
          </div>
        </button>
        </div>

        {/* Underwater Treasure Dive BONUS — unlocked after beating Corn Island */}
        <div className="relative">
          {oceanUnlocked && <div className="level-glow absolute -inset-1.5 rounded-2xl blur-lg pointer-events-none z-0" style={{ background: "#26c6da" }} aria-hidden />}
        <button
          data-testid="ocean-bonus-card"
          disabled={!oceanUnlocked}
          onClick={() => oceanUnlocked && onOceanBonus && onOceanBonus()}
          className="relative z-10 w-full pixel-card p-0 overflow-hidden text-left flex flex-col disabled:opacity-60 hover:-translate-y-1 transition-transform disabled:hover:translate-y-0"
          style={{ backgroundColor: "#fff" }}
        >
          <div className="relative h-28 sm:h-36 w-full shrink-0 overflow-hidden">
            <img src={`${base}/bonus_ocean_card.webp`} alt="Treasure Dive" draggable={false}
              className={`absolute inset-0 w-full h-full object-cover ${oceanUnlocked ? "" : "grayscale"}`} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.05))" }} />
            <span className="absolute top-2 left-3 font-pixel text-[10px] text-[#7fe9ff] pixel-stroke">★ BONUS ★</span>
            {!oceanUnlocked && <div className="absolute inset-0 flex items-center justify-center"><Lock size={44} strokeWidth={3} className="text-white" /></div>}
          </div>
          <div className="p-4 bg-white flex-1">
            <p className="font-pixel text-[10px] text-[#0fb6d6]">{t("oceanBonus")}</p>
            <p className="font-vt text-xl text-gray-900 leading-tight mt-1">{t("oceanBonusName")}</p>
            {oceanUnlocked ? (
              <span className="play-pill inline-block font-pixel text-[9px] sm:text-[10px] text-white bg-[#4CAF50] rounded-full px-3 py-1.5 pixel-stroke mt-2">▶ {t("playBonus")}</span>
            ) : (
              <p className="font-vt text-base mt-2 text-[#999]">🔒 {t("oceanLockedHint")}</p>
            )}
          </div>
        </button>
        </div>

        {/* Rodeo Bull Riding BONUS — always available */}
        <div className="relative">
          <div className="level-glow absolute -inset-1.5 rounded-2xl blur-lg pointer-events-none z-0" style={{ background: "#FFB300" }} aria-hidden />
        <button
          data-testid="rodeo-bonus-card"
          onClick={() => onRodeoBonus && onRodeoBonus()}
          className="relative z-10 w-full pixel-card p-0 overflow-hidden text-left flex flex-col hover:-translate-y-1 transition-transform"
          style={{ backgroundColor: "#fff" }}
        >
          <div className="relative h-28 sm:h-36 w-full shrink-0 overflow-hidden">
            <img src={`${base}/bonus_rodeo_card.webp`} alt="Rodeo" draggable={false}
              className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.05))" }} />
            <span className="absolute top-2 left-3 font-pixel text-[10px] text-[#FFEB3B] pixel-stroke">★ BONUS ★</span>
          </div>
          <div className="p-4 bg-white flex-1">
            <p className="font-pixel text-[10px] text-[#c0632a]">{t("rodeoBonus", "RODEO BONUS")}</p>
            <p className="font-vt text-xl text-gray-900 leading-tight mt-1">{t("rodeoBonusName", "Monta el Toro")}</p>
            <span className="play-pill inline-block font-pixel text-[9px] sm:text-[10px] text-white bg-[#4CAF50] rounded-full px-3 py-1.5 pixel-stroke mt-2">▶ {t("playBonus")}</span>
          </div>
        </button>
        </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          <button data-testid="level-back-btn" onClick={onBack} className="pixel-btn bg-[#222] text-white px-8 py-3.5 text-sm">
            {t("changeChar")}
          </button>
          <button data-testid="level-suggest-btn" onClick={onSuggest} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3.5 text-sm flex items-center gap-2">
            <Lightbulb size={16} strokeWidth={2.5} /> {t("suggestIdeas")}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
